import { useCallback, useState } from 'react';
import { operatorConsole, type OperatorMe, type PoolOfferConfig } from '../../lib/api/operatorConsole';
import { packTitle } from '../Animation/animationScope';
import { openingAccessOf, useGlobalAnimation, type OpeningAccess } from '../Animation/useGlobalAnimation';

interface Deps {
  configs: PoolOfferConfig[];
  replaceConfig: (updated: PoolOfferConfig) => void;
  flashSuccess: (message: string) => void;
  setError: (message: string | null) => void;
}

// Per-pack opening animations on the Packs page: shown only to operators allowed to set them.
export function usePackOpenings({ configs, replaceConfig, flashSuccess, setError }: Deps) {
  const [access, setAccess] = useState<OpeningAccess | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { global, loading } = useGlobalAnimation(access);

  const readAccess = useCallback((me: OperatorMe) => setAccess(openingAccessOf(me)), []);
  const close = useCallback(() => setEditingId(null), []);

  const saved = (updated: PoolOfferConfig, message: string) => {
    setError(null);
    replaceConfig(updated);
    setEditingId(null);
    flashSuccess(message);
  };

  const reset = async (packId: number) => {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(packId, { animation: null });
      replaceConfig(updated);
      flashSuccess(`${packTitle(updated)} uses the global animation`);
    } catch (err) {
      setError(`Opening animation reset failed: ${err instanceof Error ? err.message : err}`);
    }
  };

  return {
    access: access?.allowed ? access : null,
    global,
    loading,
    editing: configs.find((c) => c.pool_offer_id === editingId),
    readAccess,
    edit: setEditingId,
    close,
    saved,
    reset,
  };
}
